import { useQuery } from '@tanstack/react-query';

const IS_DEV = import.meta.env.DEV;

/**
 * Fetches and returns the field configuration for a given field type.
 *
 * Dev mode  : loads /plugin/web-sponsors-pitch/fields/{fieldType}/fields.json
 * Production: calls GET /wp-json/web-sponsors-pitch/v1/field/{fieldType}
 *
 * @param {string} fieldType  - Folder name under plugin/web-sponsors-pitch/fields/
 * @returns {{ data: Object|undefined, isLoading: boolean, isError: boolean, error: Error|null }}
 */
export function useFieldData( fieldType ) {
	return useQuery( {
		queryKey:  [ 'field', fieldType ],
		queryFn:   () => fetchField( fieldType ),
		staleTime: 5 * 60 * 1000, // field config rarely changes
		enabled:   !! fieldType,
	} );
}

async function fetchField( fieldType ) {
	if ( IS_DEV ) {
		const res = await fetch(
			`/plugin/web-sponsors-pitch/fields/${ fieldType }/fields.json?t=${ Date.now() }`
		);
		if ( ! res.ok ) {
			throw new Error( `Dev: failed to load fields/${ fieldType }/fields.json (${ res.status })` );
		}
		const config = await res.json();

		// Resolve a relative background_image_url against the field's own folder
		// so the SVG is served correctly by the Vite dev server.
		if (
			config.background_image_url &&
			! config.background_image_url.startsWith( '/' ) &&
			! config.background_image_url.startsWith( 'http' )
		) {
			config.background_image_url =
				`/plugin/web-sponsors-pitch/fields/${ fieldType }/${ config.background_image_url }`;
		}

		return config;
	}

	const base = window.WebSponsorsPitch?.restUrl ?? '/wp-json/';
	const url  = `${ base }web-sponsors-pitch/v1/field/${ encodeURIComponent( fieldType ) }`;
	const res  = await fetch( url, {
		headers: { 'X-WP-Nonce': window.WebSponsorsPitch?.nonce ?? '' },
	} );
	if ( ! res.ok ) {
		throw new Error( `Failed to fetch field '${ fieldType }': ${ res.status }` );
	}
	const data = await res.json();
	if ( ! data || ( typeof data.cols !== 'number' && typeof data.cell_width !== 'number' ) ) {
		throw new Error( `Invalid field config response for '${ fieldType }'` );
	}
	return data;
}
